import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { sweetConfirm } from "../utils/sweetAlerts";

const API = "";

function CourseFeesManager() {
  const [meta, setMeta] = useState({ courses: [], states: [] });
  const [form, setForm] = useState({ course_id: "", state_id: "", fee_amount: "", registration_fee: "", exam_fee: "" });
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({ search: "", course_id: "", state_id: "" });
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadMeta = async () => {
    const res = await axios.get(`${API}/api/course-setup/meta`);
    setMeta(res.data || { courses: [], states: [] });
  };

  const loadRows = async () => {
    const res = await axios.get(`${API}/api/course-fees-admin`, { params: filters });
    setRows(res.data || []);
  };

  useEffect(() => {
    Promise.all([loadMeta(), loadRows()]).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveFee = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.post(editingId ? `${API}/api/course-fees-admin/${editingId}` : `${API}/api/course-fees-admin`, {
        ...form,
        fee_amount: Number(form.fee_amount || 0),
        registration_fee: Number(form.registration_fee || 0),
        exam_fee: Number(form.exam_fee || 0),
      });
      setForm({ course_id: "", state_id: "", fee_amount: "", registration_fee: "", exam_fee: "" });
      setEditingId(null);
      await loadRows();
      alert(editingId ? "Course fee updated successfully." : "Course fee saved successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to save course fee.");
    } finally {
      setSaving(false);
    }
  };

  const deleteFee = async (id) => {
    if (!(await sweetConfirm("Are you sure you want to delete this course fee?", { confirmButtonText: "Yes, Delete" }))) return;
    try {
      await axios.delete(`${API}/api/course-fees-admin/${id}`);
      if (editingId === id) {
        setEditingId(null);
        setForm({ course_id: "", state_id: "", fee_amount: "", registration_fee: "", exam_fee: "" });
      }
      await loadRows();
      alert("Course fee deleted successfully.");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete course fee.");
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <h3 className="mb-1">Course Fees</h3>
            <p className="text-muted mb-4">Course fees state-wise vary করবে, তাই এখান থেকে state অনুযায়ী fee slab maintain করা যাবে।</p>
            <form onSubmit={saveFee}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Course</label>
                  <select className="form-select" value={form.course_id} onChange={(e) => setForm((prev) => ({ ...prev, course_id: e.target.value }))} required>
                    <option value="">Select Course</option>
                    {meta.courses.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">State</label>
                  <select className="form-select" value={form.state_id} onChange={(e) => setForm((prev) => ({ ...prev, state_id: e.target.value }))} required>
                    <option value="">Select State</option>
                    {meta.states.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Course Fee</label>
                  <input type="number" className="form-control" value={form.fee_amount} onChange={(e) => setForm((prev) => ({ ...prev, fee_amount: e.target.value }))} min="0" step="0.01" required />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Registration Fee</label>
                  <input type="number" className="form-control" value={form.registration_fee} onChange={(e) => setForm((prev) => ({ ...prev, registration_fee: e.target.value }))} min="0" step="0.01" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Exam Fee</label>
                  <input type="number" className="form-control" value={form.exam_fee} onChange={(e) => setForm((prev) => ({ ...prev, exam_fee: e.target.value }))} min="0" step="0.01" />
                </div>
              </div>
              <div className="mt-3">
                <button className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : editingId ? "Update Fee" : "Save Fee"}</button>
                {editingId ? <button type="button" className="btn btn-outline-secondary ms-2" onClick={() => { setEditingId(null); setForm({ course_id: "", state_id: "", fee_amount: "", registration_fee: "", exam_fee: "" }); }}>Cancel Edit</button> : null}
              </div>
            </form>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <input className="form-control" placeholder="Search course or state" value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} />
              </div>
              <div className="col-md-3">
                <select className="form-select" value={filters.course_id} onChange={(e) => setFilters((prev) => ({ ...prev, course_id: e.target.value }))}>
                  <option value="">All Courses</option>
                  {meta.courses.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <select className="form-select" value={filters.state_id} onChange={(e) => setFilters((prev) => ({ ...prev, state_id: e.target.value }))}>
                  <option value="">All States</option>
                  {meta.states.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <button className="btn btn-outline-dark w-100" onClick={loadRows}>Apply Filter</button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Course</th>
                    <th>State</th>
                    <th>Total Fee</th>
                    <th>Registration Fee</th>
                    <th>Exam Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div>{item.course_title || "-"}</div>
                        <div className="small text-muted">{item.model_name || "-"} / {item.category_name || "-"}</div>
                      </td>
                      <td>{item.state_name || "-"}</td>
                      <td>Rs {Number(item.fee_amount || 0).toFixed(2)}</td>
                      <td>Rs {Number(item.registration_fee || 0).toFixed(2)}</td>
                      <td>Rs {Number(item.exam_fee || 0).toFixed(2)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { setEditingId(item.id); setForm({ course_id: String(item.course_id || ""), state_id: String(item.state_id || ""), fee_amount: item.fee_amount || "", registration_fee: item.registration_fee || "", exam_fee: item.exam_fee || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }} title="Edit">
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteFee(item.id)} title="Delete">
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan="7" className="text-center text-muted py-4">No course fee found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CourseFeesManager;
